/** Per-tab context: what a page inside a shell tab can ask about its tab. */
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { ShellContext } from './shellContext';
import type { ReportedRun, TabApi } from './types';

const OUTSIDE_TAB: TabApi = {
  tabId: '',
  isActive: true,
  rootRef: { current: null },
  canGoBack: false,
  reportRun: () => {},
  storageKey: (k) => k,
};

const TabContext = createContext<TabApi>(OUTSIDE_TAB);

/** `TabLocationSync` feeds the tab's inner history depth through this. */
export const TabDepthContext = createContext<(depth: number) => void>(() => {});

/**
 * `document.getElementById` for a page inside a tab: every tab stays mounted,
 * so ids can repeat across tabs and a hidden tab's element must not win.
 * Outside any tab (bare mounts) there is no pane, so it falls back to the document.
 */
export function findInTab(rootRef: RefObject<HTMLElement>, id: string): HTMLElement | null {
  const root = rootRef.current;
  // `[id="…"]` rather than `#…`: jsdom's selector engine answers `#id` through the
  // document-wide getElementById, so with a duplicated id it returns the first
  // match in the document and then drops it for being outside `root`.
  return root ? root.querySelector<HTMLElement>(`[id="${CSS.escape(id)}"]`) : document.getElementById(id);
}

export function useTab(): TabApi {
  return useContext(TabContext);
}

interface TabProviderProps {
  tabId: string;
  isActive: boolean;
  rootRef: RefObject<HTMLElement>;
  children: React.ReactNode;
}

export const TabProvider: React.FC<TabProviderProps> = ({ tabId, isActive, rootRef, children }) => {
  const shell = useContext(ShellContext);
  const [canGoBack, setCanGoBack] = useState(false);
  const setDepth = useCallback((d: number) => setCanGoBack(d > 0), []);
  // The page's report effect lists this in its deps; it must keep one identity
  // for the life of the tab or every render re-reports (see UnifiedChatPanel).
  const reportRunRef = useRef(shell?.reportRun);
  reportRunRef.current = shell?.reportRun;
  const reportRun = useCallback(
    (run: ReportedRun) => reportRunRef.current?.(tabId, run),
    [tabId],
  );
  const storageKey = useCallback((k: string) => `${k}@${tabId}`, [tabId]);
  const value = useMemo<TabApi>(
    () => ({ tabId, isActive, rootRef, canGoBack, reportRun, storageKey }),
    [tabId, isActive, rootRef, canGoBack, reportRun, storageKey],
  );
  return (
    <TabContext.Provider value={value}>
      <TabDepthContext.Provider value={setDepth}>{children}</TabDepthContext.Provider>
    </TabContext.Provider>
  );
};

export default TabContext;
